import React, { useCallback, useEffect, useState } from 'react'
import type { TextEditorProps } from '../TextEditor'
import { TextEditor } from '../TextEditor'
import { debounce } from '../utils/debounce'

const DEBOUNCE_MS = 300

/**
 * @deprecated `DebouncedTextEditor` is deprecated and will be removed in a future version.
 * Use `TextEditor` directly instead — it now debounces `getData()` internally.
 *
 * A performance-optimized version of `TextEditor` with built-in debounce on the `onChange` callback.
 *
 * ## When to use `DebouncedTextEditor`
 *
 * Use this component when:
 * - The `onChange` handler triggers expensive operations (API calls, complex state updates, re-renders)
 * - You want to reduce the frequency of updates while the user is actively typing
 * - Performance is a concern in forms with multiple rich text editors
 *
 * ## How it works
 *
 * The component waits 300ms after the user stops typing before calling `onChange`.
 * If the user continues typing, the timer resets. This reduces unnecessary updates
 * while maintaining a responsive editing experience.
 *
 * @example
 * ```tsx
 * import { DebouncedTextEditor } from '@procore/text-editor'
 *
 * function MyComponent() {
 *   const [value, setValue] = React.useState('')
 *
 *   return (
 *     <DebouncedTextEditor
 *       value={value}
 *       onChange={(newValue) => setValue(newValue)}
 *     />
 *   )
 * }
 * ```
 */
export const DebouncedTextEditor = ({
  onChange,
  value,
  ...rest
}: TextEditorProps) => {
  const [internalValue, setInternalValue] = useState(value ?? '')

  const debouncedOnChange = useCallback(
    debounce((newValue: string) => {
      onChange?.(newValue)
    }, DEBOUNCE_MS),
    []
  )

  function handleChange(newValue: string) {
    setInternalValue(newValue)
    debouncedOnChange(newValue)
  }

  useEffect(() => {
    setInternalValue(value ?? '')
  }, [value])

  return <TextEditor {...rest} value={internalValue} onChange={handleChange} />
}
